'use client';

/**
 * Client Billing Page
 *
 * Customer-facing billing dashboard for client organization admins.
 * Sub-tabs: Overview, Invoices, Payment Methods, Usage, Subscription, Recharge Settings
 */

import React, { useState, useEffect, useCallback, useContext, createContext } from 'react';
import PhoneInput from '@/components/ui/PhoneInput';
import { InvoicePreviewModal } from '@/components/billing/InvoicePreviewModal';

/**
 * When ClientBillingPage is rendered for a specific organization (e.g. an admin
 * opening a client's billing from the org list), this context carries that org's
 * cbo_id so every tab scopes its API calls to that org instead of the caller's own.
 * Undefined = self-service (the caller's own org).
 */
const BillingScopeContext = createContext<number | undefined>(undefined);
const useBillingScope = () => useContext(BillingScopeContext);
import TransactionFeesCard from './TransactionFeesCard';
import OutcomeStatementCard from './OutcomeStatementCard';
import {
  CreditCard,
  FileText,
  Receipt,
  BarChart3,
  Settings,
  RefreshCw,
  Download,
  Trash2,
  Star,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Plus,
  ChevronRight,
  Zap,
  Shield,
  Clock,
  TrendingUp,
  ArrowUpRight,
} from 'lucide-react';

import {
  type BillingOverview,
  type BillingInvoice,
  type InvoiceDetail,
  type PaymentMethod,
  type UsageSummary,
  type BillingSubscription,
  type PlanTier,
  type ConsumptionThreshold,
  type PaymentRecord,
  getBillingOverview,
  getInvoices,
  getInvoiceDetail,
  payInvoice,
  getPaymentMethods,
  createSetupIntent,
  removePaymentMethod,
  setDefaultPaymentMethod,
  getUsageSummary,
  getSubscription,
  getConsumptionThresholds,
  updateConsumptionThresholds,
  manualRecharge,
} from '@/services/api/clientBillingAPI';

// ============================================================================
// Helpers
// ============================================================================

function formatCurrency(amount: number, currency = 'USD'): string {
  return `$${amount.toFixed(2)}`;
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

type InvoiceStatusType = 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';

const invoiceStatusConfig: Record<
  string,
  { label: string; bg: string; text: string }
> = {
  paid: { label: 'Paid', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300' },
  open: { label: 'Open', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
  sent: { label: 'Sent', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
  viewed: { label: 'Viewed', bg: 'bg-indigo-100 dark:bg-indigo-900/30', text: 'text-indigo-700 dark:text-indigo-300' },
  draft: { label: 'Draft', bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400' },
  void: { label: 'Void', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
  overdue: { label: 'Overdue', bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' },
  uncollectible: { label: 'Overdue', bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = invoiceStatusConfig[status] ?? invoiceStatusConfig.draft;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

const subscriptionStatusConfig: Record<string, { label: string; color: string }> = {
  active: { label: 'Active', color: 'text-green-600 dark:text-green-400' },
  trialing: { label: 'Trial', color: 'text-blue-600 dark:text-blue-400' },
  past_due: { label: 'Past Due', color: 'text-orange-600 dark:text-orange-400' },
  paused: { label: 'Paused', color: 'text-yellow-600 dark:text-yellow-400' },
  canceled: { label: 'Canceled', color: 'text-red-600 dark:text-red-400' },
  expired: { label: 'Expired', color: 'text-gray-500 dark:text-gray-400' },
};

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <RefreshCw className="w-6 h-6 text-blue-500 animate-spin" />
      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading...</span>
    </div>
  );
}

function ErrorBanner({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 flex items-center gap-3">
      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
      <p className="text-sm text-red-700 dark:text-red-300 flex-1">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="text-sm font-medium text-red-600 dark:text-red-400 hover:underline"
        >
          Retry
        </button>
      )}
    </div>
  );
}

function EmptyState({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Icon className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{description}</p>
    </div>
  );
}

// ============================================================================
// Tab Definitions
// ============================================================================

type TabKey = 'overview' | 'invoices' | 'payment-methods' | 'usage' | 'subscription' | 'recharge';

const tabs: { key: TabKey; label: string; icon: React.ElementType }[] = [
  { key: 'overview', label: 'Overview', icon: Receipt },
  { key: 'invoices', label: 'Invoices', icon: FileText },
  { key: 'payment-methods', label: 'Payment Methods', icon: CreditCard },
  { key: 'usage', label: 'Usage', icon: BarChart3 },
  { key: 'subscription', label: 'Subscription', icon: Zap },
  { key: 'recharge', label: 'Recharge Settings', icon: Settings },
];

// ============================================================================
// Overview Tab
// ============================================================================

function OverviewTab() {
  const scopedCbo = useBillingScope();
  const [data, setData] = useState<BillingOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const overview = await getBillingOverview(scopedCbo);
      setData(overview);
    } catch (err: any) {
      setError(err.message || 'Failed to load billing overview');
    } finally {
      setLoading(false);
    }
  }, [scopedCbo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} onRetry={fetchData} />;
  if (!data) return null;

  const { customer, subscription, upcoming_invoice, credit_balances, recent_payments_30d } = data;

  // Show plan info when billing tenant is not fully provisioned but tier may be assigned
  if (customer?.status === 'not_provisioned') {
    const assignedTier = (data as any)?.assigned_tier;
    const availableTiers = (data as any)?.available_tiers || [];

    return (
      <div className="space-y-6">
        {assignedTier ? (
          <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-6 space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {assignedTier.tier_name}
                </h3>
                <p className="text-sm text-gray-500">
                  {assignedTier.pricing_model?.replace('_', ' ')} plan
                  {assignedTier.billing_interval && ` \u2022 billed ${assignedTier.billing_interval}`}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {assignedTier.base_monthly_price > 0 && (
                <div>
                  <span className="text-gray-500 block">Base Price</span>
                  <span className="font-semibold">${assignedTier.base_monthly_price}/mo</span>
                </div>
              )}
              {assignedTier.per_user_price > 0 && (
                <div>
                  <span className="text-gray-500 block">Per User</span>
                  <span className="font-semibold">${assignedTier.per_user_price}/user/mo</span>
                </div>
              )}
              {assignedTier.setup_fee > 0 && (
                <div>
                  <span className="text-gray-500 block">Setup Fee</span>
                  <span className="font-semibold">${assignedTier.setup_fee}</span>
                </div>
              )}
              {assignedTier.revenue_share_config?.reimbursement_share_percent > 0 && (
                <div>
                  <span className="text-gray-500 block">Revenue Share</span>
                  <span className="font-semibold">{assignedTier.revenue_share_config.reimbursement_share_percent}% reimb.</span>
                </div>
              )}
            </div>
            {assignedTier.description && (
              <p className="text-sm text-gray-600 dark:text-gray-400">{assignedTier.description}</p>
            )}
            <p className="text-xs text-gray-400">
              Payment methods, invoices, and consumption tracking will be available once billing is fully provisioned by your platform administrator.
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-8 text-center space-y-4">
            <CreditCard className="w-12 h-12 text-blue-500 mx-auto" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Billing Not Yet Configured</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 max-w-md mx-auto">
              No plan tier has been assigned to <strong>{customer.company_name}</strong> yet.
              Contact your platform administrator to set up billing.
            </p>
          </div>
        )}

        {/* Plans are assigned by the platform operator — clients see only their
            assigned plan (above), not a self-serve plan menu. */}
      </div>
    );
  }

  const statCards = [
    {
      label: 'Current Plan',
      value: subscription?.plan_name ?? 'No Plan',
      sub: subscription ? subscriptionStatusConfig[subscription.status]?.label ?? subscription.status : '--',
      icon: Zap,
      iconColor: 'text-purple-500',
    },
    {
      label: 'Next Invoice',
      value: upcoming_invoice ? formatCurrency(upcoming_invoice.amount_due) : '--',
      sub: upcoming_invoice?.due_date ? `Due ${formatDate(upcoming_invoice.due_date)}` : 'No upcoming invoice',
      icon: FileText,
      iconColor: 'text-blue-500',
    },
    {
      label: 'Credit Balance',
      value: formatCurrency(customer.credit_balance),
      sub: Object.keys(credit_balances).length > 0
        ? Object.entries(credit_balances)
            .map(([k, v]) => `${k}: ${formatCurrency(v)}`)
            .join(' | ')
        : 'No category balances',
      icon: DollarSign,
      iconColor: 'text-green-500',
    },
    {
      label: 'Payments (30d)',
      value: formatCurrency(recent_payments_30d.total_paid),
      sub: `${recent_payments_30d.count} payment${recent_payments_30d.count !== 1 ? 's' : ''}`,
      icon: TrendingUp,
      iconColor: 'text-indigo-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div
            key={card.label}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 shadow-sm"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {card.label}
              </span>
              <card.icon className={`w-5 h-5 ${card.iconColor}`} />
            </div>
            <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">{card.value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{card.sub}</p>
          </div>
        ))}
      </div>

      {/* Customer Info */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Account Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Company</span>
            <p className="font-medium text-gray-900 dark:text-gray-100">{customer.company_name}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Billing Email</span>
            <p className="font-medium text-gray-900 dark:text-gray-100">{customer.billing_email}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Currency</span>
            <p className="font-medium text-gray-900 dark:text-gray-100">{customer.currency.toUpperCase()}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Account Status</span>
            <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">{customer.status}</p>
          </div>
        </div>
      </div>

      {/* Transaction fees (effective per-org fees) */}
      <TransactionFeesCard cboId={scopedCbo} />

      {/* Subscription Summary */}
      {subscription && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Current Subscription</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Plan</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">{subscription.plan_name}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Billing Interval</span>
              <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">{subscription.billing_interval}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Current Period</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
              </p>
            </div>
            {subscription.mrr != null && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">MRR</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(subscription.mrr)}</p>
              </div>
            )}
            <div>
              <span className="text-gray-500 dark:text-gray-400">Seats</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">{subscription.quantity}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Auto-Renew</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">{subscription.auto_renew ? 'Yes' : 'No'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Invoices Tab
// ============================================================================

function InvoicesTab() {
  const scopedCbo = useBillingScope();
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<BillingInvoice[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payingId, setPayingId] = useState<string | null>(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getInvoices({
        status: statusFilter || undefined,
        page,
        page_size: pageSize,
        cboId: scopedCbo,
      });
      setInvoices(result.invoices);
      setTotal(result.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, page, pageSize, scopedCbo]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);


  const handlePayInvoice = async (invoiceId: string) => {
    setPayingId(invoiceId);
    try {
      const { portal_url } = await payInvoice(invoiceId);
      if (portal_url) {
        window.open(portal_url, '_blank', 'noopener,noreferrer');
      } else {
        alert('No online payment link is available for this invoice yet. Please contact your account manager.');
      }
    } catch (err: any) {
      alert(`Payment failed: ${err.message}`);
    } finally {
      setPayingId(null);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <>
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="sent">Sent</option>
            <option value="viewed">Viewed</option>
            <option value="overdue">Overdue</option>
            <option value="paid">Paid</option>
            <option value="void">Void</option>
          </select>
        </div>
        <button
          onClick={fetchInvoices}
          className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {error && <ErrorBanner message={error} onRetry={fetchInvoices} />}

      {loading ? (
        <LoadingSpinner />
      ) : invoices.length === 0 ? (
        <EmptyState icon={FileText} title="No invoices" description="No invoices match the current filter." />
      ) : (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Invoice #</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Due Date</th>
                <th className="text-left px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Status</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Total</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Amount Due</th>
                <th className="text-right px-4 py-3 font-medium text-gray-500 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/30 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                    {inv.invoice_number}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{formatDate(inv.invoice_date)}</td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{formatDate(inv.due_date)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={inv.status} />
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">
                    {formatCurrency(inv.total)}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">
                    {formatCurrency(inv.amount_due)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* View the formatted invoice (same rendering as the
                          operator invoice page); print/save-PDF from there. */}
                      <button
                        onClick={() => setPreviewId(String(inv.id))}
                        className="p-1.5 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        title="View invoice"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {['open', 'sent', 'viewed', 'overdue', 'uncollectible'].includes(inv.status) &&
                        inv.portal_url && (
                        <button
                          onClick={() => handlePayInvoice(inv.id)}
                          disabled={payingId === inv.id}
                          className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 transition-colors"
                        >
                          {payingId === inv.id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <DollarSign className="w-3 h-3" />
                          )}
                          Pay
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Previous
                </button>
                <span className="px-2 text-xs text-gray-500 dark:text-gray-400">
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    {previewId && (
      <InvoicePreviewModal invoiceId={previewId} cboId={scopedCbo} onClose={() => setPreviewId(null)} />
    )}
    </>
  );
}

// ============================================================================
// Payment Methods Tab
// ============================================================================

function PaymentMethodsTab() {
  const scopedCbo = useBillingScope();
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [defaultId, setDefaultId] = useState<string | undefined>();
  const [info, setInfo] = useState<import('@/services/api/clientBillingAPI').PaymentInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);

  const fetchMethods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { getPaymentInfo } = await import('@/services/api/clientBillingAPI');
      const [result, pinfo] = await Promise.all([
        getPaymentMethods().catch(() => ({ payment_methods: [], default_id: undefined })),
        getPaymentInfo(scopedCbo),
      ]);
      setMethods(result.payment_methods || []);
      setDefaultId(result.default_id);
      setInfo(pinfo);
    } catch (err: any) {
      setError(err.message || 'Failed to load payment methods');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  const handleRemove = async (id: string) => {
    if (!confirm('Remove this payment method? This cannot be undone.')) return;
    setRemovingId(id);
    try {
      await removePaymentMethod(id);
      await fetchMethods();
    } catch (err: any) {
      alert(`Failed to remove: ${err.message}`);
    } finally {
      setRemovingId(null);
    }
  };

  const handleSetDefault = async (id: string) => {
    setSettingDefaultId(id);
    try {
      await setDefaultPaymentMethod(id);
      setDefaultId(id);
    } catch (err: any) {
      alert(`Failed to set default: ${err.message}`);
    } finally {
      setSettingDefaultId(null);
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      const { createCardSetupSession } = await import('@/services/api/clientBillingAPI');
      const { url } = await createCardSetupSession(scopedCbo);
      if (url) {
        // Redirect to Stripe's hosted card-setup page; the card attaches to the
        // customer and appears in the list on return.
        window.location.href = url;
      } else {
        alert('Card setup is unavailable right now. Please pay by ACH/check.');
      }
    } catch (err: any) {
      alert(`Failed to start card setup: ${err.message}`);
    }
  };

  function methodLabel(m: PaymentMethod): string {
    if (m.method_type === 'card') {
      return `${m.card_brand ?? 'Card'} ending in ${m.card_last_four ?? '****'}`;
    }
    if (m.method_type === 'bank_account' || m.method_type === 'ach') {
      return `${m.bank_name ?? 'Bank'} ending in ${m.account_last_four ?? '****'}`;
    }
    return m.method_type.replace('_', ' ');
  }

  function methodExpiry(m: PaymentMethod): string {
    if (m.card_exp_month && m.card_exp_year) {
      return `${String(m.card_exp_month).padStart(2, '0')}/${m.card_exp_year}`;
    }
    return '';
  }

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} onRetry={fetchMethods} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Saved Payment Methods
        </h3>
        {info?.card_setup_available && (
          <button
            onClick={handleAddPaymentMethod}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Payment Method
          </button>
        )}
      </div>

      {info && !info.card_setup_available && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
          Online card payment isn&apos;t enabled for your organization yet. Please pay by bank
          transfer (ACH) or check using the details below, or the payment link on each invoice.
        </div>
      )}

      {info && (info.ach.available || info.check.available || info.payment_instructions) && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm space-y-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Other ways to pay</h3>
          {info.ach.available && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Bank transfer / ACH</p>
              <div className="text-sm text-gray-700 dark:text-gray-300 space-y-0.5">
                {info.ach.bank_name && <div>Bank: <span className="font-medium">{info.ach.bank_name}</span></div>}
                {info.ach.account_number && <div>Account #: <span className="font-medium">{info.ach.account_number}</span></div>}
                {info.ach.routing_number && <div>Routing #: <span className="font-medium">{info.ach.routing_number}</span></div>}
                {info.ach.ach_instructions && <div className="text-gray-500 whitespace-pre-line mt-1">{info.ach.ach_instructions}</div>}
              </div>
            </div>
          )}
          {info.check.available && (
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Check</p>
              <div className="text-sm text-gray-700 dark:text-gray-300 space-y-0.5">
                {info.check.payable_to && <div>Payable to: <span className="font-medium">{info.check.payable_to}</span></div>}
                {info.check.address && <div className="whitespace-pre-line">Mail to: {info.check.address}</div>}
                {info.check.instructions && <div className="text-gray-500 whitespace-pre-line mt-1">{info.check.instructions}</div>}
              </div>
            </div>
          )}
          {info.payment_instructions && (
            <p className="text-sm text-gray-500 dark:text-gray-400 whitespace-pre-line border-t border-gray-100 dark:border-gray-700 pt-3">{info.payment_instructions}</p>
          )}
        </div>
      )}

      {methods.length === 0 ? (
        <EmptyState
          icon={CreditCard}
          title="No payment methods"
          description="Add a payment method to pay invoices and enable auto-recharge."
        />
      ) : (
        <div className="space-y-3">
          {methods.map((m) => (
            <div
              key={m.id}
              className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm"
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                      {methodLabel(m)}
                    </span>
                    {m.id === defaultId && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded-full">
                        <Star className="w-3 h-3" />
                        Default
                      </span>
                    )}
                  </div>
                  {methodExpiry(m) && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Expires {methodExpiry(m)}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {m.id !== defaultId && (
                  <button
                    onClick={() => handleSetDefault(m.id)}
                    disabled={settingDefaultId === m.id}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
                  >
                    {settingDefaultId === m.id ? (
                      <RefreshCw className="w-3 h-3 animate-spin" />
                    ) : (
                      <Star className="w-3 h-3" />
                    )}
                    Set Default
                  </button>
                )}
                <button
                  onClick={() => handleRemove(m.id)}
                  disabled={removingId === m.id}
                  className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                  title="Remove"
                >
                  {removingId === m.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Usage Tab
// ============================================================================

function UsageTab() {
  const scopedCbo = useBillingScope();
  const [data, setData] = useState<UsageSummary | null>(null);
  const [plan, setPlan] = useState<import('@/services/api/clientBillingAPI').PlanUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { getPlanUsage } = await import('@/services/api/clientBillingAPI');
      // Plan + headcount + recurring always works; the metered summary depends on
      // billing provisioning, so tolerate its failure.
      const [pu, usage] = await Promise.all([
        getPlanUsage(scopedCbo),
        getUsageSummary(period || undefined).catch(() => null),
      ]);
      setPlan(pu);
      setData(usage);
    } catch (err: any) {
      setError(err.message || 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [period, scopedCbo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} onRetry={fetchData} />;

  const money = (n: number) => formatCurrency(n || 0);
  const maxUsage = Math.max(...(data?.usage || []).map((u) => u.total_usage), 1);
  const rec = plan?.recurring;
  const tier = plan?.assigned_tier;

  return (
    <div className="space-y-6">
      {/* Plan & recurring (works without billing provisioning) */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Your plan &amp; usage</h3>
        {!tier ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No plan is currently assigned to your organization.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              <div><p className="text-xs text-gray-500 dark:text-gray-400">Plan</p><p className="text-base font-semibold text-gray-900 dark:text-gray-100">{tier.tier_name}</p></div>
              <div><p className="text-xs text-gray-500 dark:text-gray-400">Staff (seats)</p><p className="text-base font-semibold text-gray-900 dark:text-gray-100">{plan?.counts?.seats ?? 0}</p></div>
              <div><p className="text-xs text-gray-500 dark:text-gray-400">Active members</p><p className="text-base font-semibold text-gray-900 dark:text-gray-100">{plan?.counts?.active_members ?? 0}</p></div>
              <div><p className="text-xs text-gray-500 dark:text-gray-400">{rec?.annual ? 'Annual fee' : 'Recurring / mo'}</p><p className="text-base font-semibold text-gray-900 dark:text-gray-100">{money(rec?.annual ? rec?.annual_amount : rec?.monthly_total || 0)}</p></div>
            </div>
            {rec && (
              <div className="text-sm text-gray-500 dark:text-gray-400 space-y-0.5 border-t border-gray-100 dark:border-gray-700 pt-3">
                {rec.base > 0 && <div className="flex justify-between"><span>{rec.annual ? 'Annual fee (billed once)' : 'Base'}</span><span className="font-medium text-gray-700 dark:text-gray-300">{money(rec.annual ? rec.annual_amount : rec.base)}{rec.annual ? '/yr' : '/mo'}</span></div>}
                {rec.staff_rate > 0 && <div className="flex justify-between"><span>Staff · {rec.staff_count} × {money(rec.staff_rate)}</span><span className="font-medium text-gray-700 dark:text-gray-300">{money(rec.staff_amount)}/mo</span></div>}
                {rec.member_rate > 0 && <div className="flex justify-between"><span>Members · {rec.member_count} × {money(rec.member_rate)}</span><span className="font-medium text-gray-700 dark:text-gray-300">{money(rec.member_amount)}/mo</span></div>}
              </div>
            )}
          </>
        )}
      </div>

      {/* Metered consumption (from plan-usage; may be empty until provisioned) */}
      {plan && plan.metered_usage.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Consumption this month</h3>
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-400"><tr><th className="text-left py-1">Meter</th><th className="text-right py-1">Used</th><th className="text-right py-1">Amount</th></tr></thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {plan.metered_usage.map((m) => (
                <tr key={m.meter_code}>
                  <td className="py-1.5 text-gray-700 dark:text-gray-300">{m.meter_name || m.meter_code}</td>
                  <td className="py-1.5 text-right text-gray-500">{Number(m.billable_usage).toLocaleString()}</td>
                  <td className="py-1.5 text-right font-medium text-gray-900 dark:text-gray-100">{money(m.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Period selector */}
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Period:</label>
        <input
          type="month"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Credit Balances */}
      {data && Object.keys(data.credit_balances).length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Credit Balances</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {Object.entries(data.credit_balances).map(([category, balance]) => (
              <div key={category} className="text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{category.replace('_', ' ')}</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">{formatCurrency(balance)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Usage Meters (metered detail — present once billing is provisioned) */}
      {data && data.usage.length > 0 && (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Usage Meters</h3>
        {(
          <div className="space-y-4">
            {data.usage.map((meter) => {
              const pct = Math.min((meter.total_usage / maxUsage) * 100, 100);
              return (
                <div key={meter.meter_code}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{meter.meter_name}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {meter.total_usage.toLocaleString()} ({meter.event_count.toLocaleString()} events)
                    </span>
                  </div>
                  <div className="w-full h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* Communication Pricing */}
      {data && data.communication_pricing.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Communication Usage</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 font-medium text-gray-500 dark:text-gray-400">Channel</th>
                  <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">Cost/Unit</th>
                  <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">Included</th>
                  <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">Threshold</th>
                  <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">Overage Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {data.communication_pricing.map((cp) => (
                  <tr key={cp.channel}>
                    <td className="py-2 text-gray-900 dark:text-gray-100">{cp.display_name}</td>
                    <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                      {formatCurrency(cp.cost_per_unit)} / {cp.unit_name}
                    </td>
                    <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                      {cp.included_units.toLocaleString()}
                    </td>
                    <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                      {cp.customer_threshold.toLocaleString()}
                    </td>
                    <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                      {cp.overage_rate != null ? formatCurrency(cp.overage_rate) : '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* AI Token Pricing */}
      {data && data.ai_pricing.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">AI Token Usage</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 font-medium text-gray-500 dark:text-gray-400">Service</th>
                  <th className="text-left py-2 font-medium text-gray-500 dark:text-gray-400">Model</th>
                  <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">Input $/1M</th>
                  <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">Output $/1M</th>
                  <th className="text-right py-2 font-medium text-gray-500 dark:text-gray-400">Included Credits/mo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {data.ai_pricing.map((ai) => (
                  <tr key={ai.service_code}>
                    <td className="py-2 text-gray-900 dark:text-gray-100">{ai.service_name}</td>
                    <td className="py-2 text-gray-600 dark:text-gray-400">{ai.model_name ?? '--'}</td>
                    <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                      {formatCurrency(ai.input_rate_per_million)}
                    </td>
                    <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                      {formatCurrency(ai.output_rate_per_million)}
                    </td>
                    <td className="py-2 text-right text-gray-600 dark:text-gray-400">
                      {formatCurrency(ai.included_credits_per_month)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <OutcomeStatementCard cboId={scopedCbo} />
    </div>
  );
}

// ============================================================================
// Subscription Tab
// ============================================================================

function SubscriptionTab() {
  const [subscription, setSubscription] = useState<BillingSubscription | null>(null);
  const [availableTiers, setAvailableTiers] = useState<PlanTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getSubscription();
      setSubscription(result.subscription);
      setAvailableTiers(result.available_tiers);
    } catch (err: any) {
      setError(err.message || 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} onRetry={fetchData} />;

  const subStatus = subscription
    ? subscriptionStatusConfig[subscription.status] ?? { label: subscription.status, color: 'text-gray-500' }
    : null;

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      {subscription ? (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Current Plan</h3>
            <span className={`text-sm font-medium ${subStatus?.color}`}>{subStatus?.label}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Plan Name</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">{subscription.plan_name}</p>
            </div>
            {subscription.base_price != null && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Base Price</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {formatCurrency(subscription.base_price)} / {subscription.billing_interval}
                </p>
              </div>
            )}
            {subscription.unit_price != null && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Per {subscription.plan_unit_name ?? 'User'}</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(subscription.unit_price)}</p>
              </div>
            )}
            <div>
              <span className="text-gray-500 dark:text-gray-400">Seats</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">{subscription.quantity}</p>
            </div>
            {subscription.included_units != null && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Included Units</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">{subscription.included_units.toLocaleString()}</p>
              </div>
            )}
            <div>
              <span className="text-gray-500 dark:text-gray-400">Billing Interval</span>
              <p className="font-medium text-gray-900 dark:text-gray-100 capitalize">{subscription.billing_interval}</p>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Current Period</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
              </p>
            </div>
            {subscription.trial_end && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Trial Ends</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(subscription.trial_end)}</p>
              </div>
            )}
            {subscription.contract_end && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Contract Ends</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">{formatDate(subscription.contract_end)}</p>
              </div>
            )}
            <div>
              <span className="text-gray-500 dark:text-gray-400">Auto-Renew</span>
              <p className="font-medium text-gray-900 dark:text-gray-100">{subscription.auto_renew ? 'Yes' : 'No'}</p>
            </div>
            {subscription.setup_fee != null && subscription.setup_fee > 0 && (
              <div>
                <span className="text-gray-500 dark:text-gray-400">Setup Fee</span>
                <p className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(subscription.setup_fee)}</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm text-center">
          <Zap className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No plan assigned yet. Your platform operator will assign a plan to your organization.</p>
        </div>
      )}

    </div>
  );
}

// ============================================================================
// Recharge Settings Tab
// ============================================================================

interface ThresholdFormState {
  service_category: 'communication' | 'ai_token';
  low_balance_threshold: string;
  auto_recharge_threshold: string;
  recharge_amount: string;
  max_monthly_cap: string;
  alert_enabled: boolean;
  alert_email: boolean;
  alert_sms: boolean;
  alert_email_address: string;
  alert_phone: string;
  alert_at_percentage: string;
  is_enabled: boolean;
  payment_method_id: string;
}

const defaultFormState: ThresholdFormState = {
  service_category: 'communication',
  low_balance_threshold: '',
  auto_recharge_threshold: '',
  recharge_amount: '',
  max_monthly_cap: '',
  alert_enabled: true,
  alert_email: true,
  alert_sms: false,
  alert_email_address: '',
  alert_phone: '',
  alert_at_percentage: '20',
  is_enabled: false,
  payment_method_id: '',
};

function RechargeSettingsTab() {
  const [thresholds, setThresholds] = useState<ConsumptionThreshold[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [form, setForm] = useState<ThresholdFormState>(defaultFormState);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [thresholdResult, pmResult] = await Promise.all([
        getConsumptionThresholds(),
        getPaymentMethods(),
      ]);
      setThresholds(thresholdResult.thresholds);
      setPaymentMethods(pmResult.payment_methods);

      // Pre-populate form if thresholds exist for the selected category
      const existing = thresholdResult.thresholds.find(
        (t) => t.service_category === form.service_category
      );
      if (existing) {
        populateForm(existing);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load recharge settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function populateForm(t: ConsumptionThreshold) {
    setForm({
      service_category: t.service_category,
      low_balance_threshold: t.low_balance_threshold?.toString() ?? '',
      auto_recharge_threshold: t.auto_recharge_threshold?.toString() ?? '',
      recharge_amount: t.recharge_amount?.toString() ?? '',
      max_monthly_cap: t.max_monthly_cap?.toString() ?? '',
      alert_enabled: t.alert_enabled,
      alert_email: t.alert_channels?.email ?? true,
      alert_sms: t.alert_channels?.sms ?? false,
      alert_email_address: t.alert_email ?? '',
      alert_phone: t.alert_phone ?? '',
      alert_at_percentage: t.alert_at_percentage?.toString() ?? '20',
      is_enabled: t.is_enabled,
      payment_method_id: t.payment_method_id ?? '',
    });
  }

  const handleCategoryChange = (category: 'communication' | 'ai_token') => {
    const existing = thresholds.find((t) => t.service_category === category);
    if (existing) {
      populateForm(existing);
    } else {
      setForm({ ...defaultFormState, service_category: category });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      await updateConsumptionThresholds({
        service_category: form.service_category,
        low_balance_threshold: form.low_balance_threshold ? parseFloat(form.low_balance_threshold) : undefined,
        auto_recharge_threshold: form.auto_recharge_threshold ? parseFloat(form.auto_recharge_threshold) : undefined,
        recharge_amount: form.recharge_amount ? parseFloat(form.recharge_amount) : undefined,
        max_monthly_cap: form.max_monthly_cap ? parseFloat(form.max_monthly_cap) : undefined,
        alert_enabled: form.alert_enabled,
        alert_channels: { email: form.alert_email, sms: form.alert_sms },
        alert_email: form.alert_email_address || undefined,
        alert_phone: form.alert_phone || undefined,
        alert_at_percentage: form.alert_at_percentage ? parseFloat(form.alert_at_percentage) : undefined,
        is_enabled: form.is_enabled,
        payment_method_id: form.payment_method_id || undefined,
      });
      setSaveMessage('Settings saved successfully.');
      await fetchData();
    } catch (err: any) {
      setSaveMessage(`Error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleManualRecharge = async () => {
    const amount = parseFloat(form.recharge_amount);
    if (!amount || amount <= 0) {
      alert('Enter a valid recharge amount first.');
      return;
    }
    if (!confirm(`Recharge ${formatCurrency(amount)} for ${form.service_category.replace('_', ' ')}?`)) return;
    try {
      await manualRecharge({
        service_category: form.service_category,
        amount,
        payment_method_id: form.payment_method_id || undefined,
      });
      alert('Recharge successful!');
      await fetchData();
    } catch (err: any) {
      alert(`Recharge failed: ${err.message}`);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorBanner message={error} onRetry={fetchData} />;

  const existingThreshold = thresholds.find((t) => t.service_category === form.service_category);

  return (
    <div className="space-y-6">
      {/* Existing thresholds summary */}
      {thresholds.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Active Thresholds</h3>
          <div className="space-y-3">
            {thresholds.map((t) => (
              <div
                key={t.id}
                className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50"
              >
                <div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 capitalize">
                    {t.service_category.replace('_', ' ')}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Auto-recharge at {formatCurrency(t.auto_recharge_threshold ?? 0)} | Recharge {formatCurrency(t.recharge_amount ?? 0)} | Cap {formatCurrency(t.max_monthly_cap ?? 0)}/mo
                  </p>
                  {t.last_recharge_at && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      Last recharge: {formatDate(t.last_recharge_at)} ({formatCurrency(t.last_recharge_amount ?? 0)})
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      t.is_enabled
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {t.is_enabled ? 'Active' : 'Disabled'}
                  </span>
                  <button
                    onClick={() => handleCategoryChange(t.service_category)}
                    className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-6">
          {existingThreshold ? 'Edit' : 'Configure'} Recharge Threshold
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Service Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Service Category
            </label>
            <select
              value={form.service_category}
              onChange={(e) => handleCategoryChange(e.target.value as 'communication' | 'ai_token')}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="communication">Communication</option>
              <option value="ai_token">AI Token</option>
            </select>
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Payment Method
            </label>
            <select
              value={form.payment_method_id}
              onChange={(e) => setForm({ ...form, payment_method_id: e.target.value })}
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Default</option>
              {paymentMethods.map((pm) => (
                <option key={pm.id} value={pm.id}>
                  {pm.method_type === 'card'
                    ? `${pm.card_brand ?? 'Card'} ****${pm.card_last_four ?? ''}`
                    : `${pm.bank_name ?? 'Bank'} ****${pm.account_last_four ?? ''}`}
                </option>
              ))}
            </select>
          </div>

          {/* Low Balance Threshold */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Low Balance Alert Threshold ($)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.low_balance_threshold}
              onChange={(e) => setForm({ ...form, low_balance_threshold: e.target.value })}
              placeholder="e.g. 10.00"
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Auto-Recharge Threshold */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Auto-Recharge When Below ($)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.auto_recharge_threshold}
              onChange={(e) => setForm({ ...form, auto_recharge_threshold: e.target.value })}
              placeholder="e.g. 5.00"
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Recharge Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Recharge Amount ($)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.recharge_amount}
              onChange={(e) => setForm({ ...form, recharge_amount: e.target.value })}
              placeholder="e.g. 50.00"
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Max Monthly Cap */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Max Monthly Recharge Cap ($)
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.max_monthly_cap}
              onChange={(e) => setForm({ ...form, max_monthly_cap: e.target.value })}
              placeholder="e.g. 500.00"
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Alert at Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Alert at Balance Percentage (%)
            </label>
            <input
              type="number"
              min="1"
              max="100"
              step="1"
              value={form.alert_at_percentage}
              onChange={(e) => setForm({ ...form, alert_at_percentage: e.target.value })}
              placeholder="e.g. 20"
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Alert Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Alert Email Address
            </label>
            <input
              type="email"
              value={form.alert_email_address}
              onChange={(e) => setForm({ ...form, alert_email_address: e.target.value })}
              placeholder="billing@company.com"
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Alert Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Alert Phone Number
            </label>
            <PhoneInput
              value={form.alert_phone}
              onChange={(v) => setForm({ ...form, alert_phone: v })}
              placeholder="+1234567890"
              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Toggles */}
        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Enable Auto-Recharge</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Automatically recharge when balance drops below threshold</p>
            </div>
            <button
              type="button"
              onClick={() => setForm({ ...form, is_enabled: !form.is_enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.is_enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.is_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Enable Alerts</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Receive notifications when balance is low</p>
            </div>
            <button
              type="button"
              onClick={() => setForm({ ...form, alert_enabled: !form.alert_enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.alert_enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.alert_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {form.alert_enabled && (
            <div className="flex items-center gap-6 pl-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={form.alert_email}
                  onChange={(e) => setForm({ ...form, alert_email: e.target.checked })}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                Email
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={form.alert_sms}
                  onChange={(e) => setForm({ ...form, alert_sms: e.target.checked })}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
                SMS
              </label>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
          <button
            onClick={handleManualRecharge}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Manual Recharge
          </button>
          <div className="flex items-center gap-3">
            {saveMessage && (
              <span
                className={`text-xs ${
                  saveMessage.startsWith('Error') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                }`}
              >
                {saveMessage}
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Main Page Component
// ============================================================================

export default function ClientBillingPage({ organizationId }: { organizationId?: number } = {}) {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  // When scoped to another org (admin viewing a client), Subscription/Recharge are
  // self-service knobs whose APIs aren't cbo-scoped — hide them to avoid showing the
  // viewer's own data under the client's name.
  const visibleTabs = organizationId
    ? tabs.filter((t) => t.key !== 'subscription' && t.key !== 'recharge')
    : tabs;

  return (
    <BillingScopeContext.Provider value={organizationId}>
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-1 overflow-x-auto" aria-label="Billing tabs">
          {visibleTabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'invoices' && <InvoicesTab />}
        {activeTab === 'payment-methods' && <PaymentMethodsTab />}
        {activeTab === 'usage' && <UsageTab />}
        {activeTab === 'subscription' && <SubscriptionTab />}
        {activeTab === 'recharge' && <RechargeSettingsTab />}
      </div>
    </div>
    </BillingScopeContext.Provider>
  );
}
